import { Component, OnInit } from '@angular/core';
import { CheckoutStepComponent } from '../base/checkout-step.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs';
import { Customer, CustomerGungMappedEntityAddress } from '../../../../models/customer';
import { SelectedCustomerService } from '../../../../services/selected-customer/selected-customer.service';

@Component({
  selector: 'lib-delivery-locations',
  templateUrl: './delivery-locations.component.html',
  styleUrl: './delivery-locations.component.scss'
})
export class DeliveryLocationsComponent extends CheckoutStepComponent implements OnInit {
  form: FormGroup;
  selectedCustomer: Customer;
  alert: { message: string; type: string };

  deliveryAddresses: CustomerGungMappedEntityAddress[];
  // The selected delivery address id. If a number it is a predefined address. If it is undefined it is a custom address.
  selectedDeliveryAddressIndex: number | undefined;
  selectedDeliveryAddress: CustomerGungMappedEntityAddress;

  editDeliveryAddressEnabled: boolean = false;

  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected formBuilder: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(selectedCustomer => {
        this.selectedCustomer = selectedCustomer;
        this.deliveryAddresses = selectedCustomer.extra?.gme?.deliveryAddresses || [];
        this.initSelectedDeliveryAddress();
        this.initForm();
      });
  }

  public getLabel(): string {
    return 'DELIVERY_LOCATION';
  }

  initSelectedDeliveryAddress() {
    const hasSelectedDeliveryAddressSinceBefore: boolean =
      !!this.checkout.extra?.gme?.deliveryAddress?.address1 || !!this.checkout.extra?.gme?.deliveryAddress?.name;

    // There is 2 cases we need to care of if we have a selected delivery address since before. The first one is that
    // we have a selected delivery address from the delivery addresses, the other is that we have modified manually a
    // address.
    if (hasSelectedDeliveryAddressSinceBefore) {
      // We have a delivery address selected since before, we need to check if it is in the list of delivery addresses
      // and if so, we need to select it.
      const selectedDeliveryAddressIndex = this.deliveryAddresses.findIndex(address => {
        return (
          address.name === this.checkout.extra?.gme?.deliveryAddress?.name &&
          address.address1 === this.checkout.extra?.gme?.deliveryAddress?.address1 &&
          address.address2 === this.checkout.extra?.gme?.deliveryAddress?.address2 &&
          address.phoneNo === this.checkout.extra?.gme?.deliveryAddress?.phoneNo &&
          address.zipCode === this.checkout.extra?.gme?.deliveryAddress?.zipCode &&
          address.city === this.checkout.extra?.gme?.deliveryAddress?.city &&
          address.countryCode === this.checkout.extra?.gme?.deliveryAddress?.countryCode
        );
      });
      if (selectedDeliveryAddressIndex >= 0) {
        this.selectedDeliveryAddressIndex = selectedDeliveryAddressIndex;
        this.selectedDeliveryAddress = this.deliveryAddresses[selectedDeliveryAddressIndex];
        return;
      } else {
        this.selectedDeliveryAddressIndex = undefined;
        this.selectedDeliveryAddress = {
          name: this.checkout.extra?.gme?.deliveryAddress?.name || '',
          address1: this.checkout.extra?.gme?.deliveryAddress?.address1 || '',
          address2: this.checkout.extra?.gme?.deliveryAddress?.address2 || '',
          zipCode: this.checkout.extra?.gme?.deliveryAddress?.zipCode || '',
          city: this.checkout.extra?.gme?.deliveryAddress?.city || '',
          countryCode: this.checkout.extra?.gme?.deliveryAddress?.countryCode || '',
          email: '',
          phoneNo: this.checkout.extra?.gme?.deliveryAddress?.phoneNo || ''
        } as CustomerGungMappedEntityAddress;
        return;
      }
    }

    if (this.deliveryAddresses.length > 0) {
      this.selectedDeliveryAddressIndex = 0;
      this.selectedDeliveryAddress = this.deliveryAddresses[0];
    } else {
      this.selectedDeliveryAddressIndex = undefined;
      this.selectedDeliveryAddress = {
        name: this.checkout.extra?.gme?.deliveryAddress?.name || this.selectedCustomer.extra?.gme?.address?.name || '',
        address1:
          this.checkout.extra?.gme?.deliveryAddress?.address1 ||
          this.selectedCustomer.extra?.gme?.address?.address1 ||
          '',
        address2:
          this.checkout.extra?.gme?.deliveryAddress?.address2 ||
          this.selectedCustomer.extra?.gme?.address?.address2 ||
          '',
        zipCode:
          this.checkout.extra?.gme?.deliveryAddress?.zipCode ||
          this.selectedCustomer.extra?.gme?.address?.zipCode ||
          '',
        city: this.checkout.extra?.gme?.deliveryAddress?.city || this.selectedCustomer.extra?.gme?.address?.city || '',
        countryCode:
          this.checkout.extra?.gme?.deliveryAddress?.countryCode ||
          this.selectedCustomer.extra?.gme?.address?.countryCode ||
          '',
        email: '',
        phoneNo:
          this.checkout.extra?.gme?.deliveryAddress?.phoneNo || this.selectedCustomer.extra?.gme?.address?.phoneNo || ''
      } as CustomerGungMappedEntityAddress;
    }
  }

  initForm() {
    this.form = this.formBuilder.group({
      deliveryAddress: this.formBuilder.group({
        name: this.formBuilder.control(this.selectedDeliveryAddress.name),
        address1: this.formBuilder.control(this.selectedDeliveryAddress.address1),
        address2: this.formBuilder.control(this.selectedDeliveryAddress.address2),
        phoneNo: this.formBuilder.control(this.selectedDeliveryAddress.phoneNo, [Validators.pattern(/^[^a-zA-Z]*$/)]),
        zipCode: this.formBuilder.control(this.selectedDeliveryAddress.zipCode),
        city: this.formBuilder.control(this.selectedDeliveryAddress.city),
        countryCode: this.formBuilder.control(this.selectedDeliveryAddress.countryCode)
      }),
      message: this.formBuilder.control(this.checkout.extra?.gme?.deliveryComment || ''),
      externalReference: this.formBuilder.control(this.checkout.extra?.gme?.externalReference || '')
    });
  }

  protected handleNextButtonClicked(): void {
    if (this.form && this.form.invalid) {
      this.form.markAllAsTouched();
      this.alert = {
        message: 'FORM_CONTAINS_ERROR',
        type: 'warning'
      };
      return;
    }

    this.assignFormValues(this.form);
    this.stepDone.emit(this.checkout);
  }

  assignFormValues(form: FormGroup) {
    const rawFormData = form.getRawValue();
    console.log('rawFormData', rawFormData);
    this.checkout.extra = {
      ...this.checkout.extra,
      gme: {
        ...this.checkout.extra.gme,
        ...rawFormData
      }
    };
  }

  protected deliveryLocationSelected(index: number | undefined): void {
    if (index === undefined) {
      this.selectedDeliveryAddressIndex = undefined;
      this.form.get('deliveryAddress').patchValue({
        name: '',
        address1: '',
        address2: '',
        phoneNo: '',
        zipCode: '',
        city: '',
        countryCode: ''
      });
      return;
    }

    this.selectedDeliveryAddressIndex = index;
    this.selectedDeliveryAddress = this.deliveryAddresses[index];
    this.form.get('deliveryAddress').patchValue({
      name: this.selectedDeliveryAddress.name,
      address1: this.selectedDeliveryAddress.address1,
      address2: this.selectedDeliveryAddress.address2,
      phoneNo: this.selectedDeliveryAddress.phoneNo,
      zipCode: this.selectedDeliveryAddress.zipCode,
      city: this.selectedDeliveryAddress.city,
      countryCode: this.selectedDeliveryAddress.countryCode
    });
  }

  public changeEditStatus(event) {
    if (!event.target.checked) {
      // DISABLE EDIT //
      this.editDeliveryAddressEnabled = false;
    } else {
      // ENABLE EDIT //
      this.editDeliveryAddressEnabled = true;
    }
  }
}
